import React, { useState, useEffect } from 'react';
// import { WebSocketProvider } from './contexts/WebSocketContext';  // Commented out - not currently used
import PracticeMode from './components/PracticeMode';
import MonitorMode from './components/MonitorMode';
import AdminPanel from './components/AdminPanel';
import Settings from './components/Settings';
import Studio from './components/Studio';
import StudioView from './components/StudioView';
import Login from './components/Auth/Login';
import SignUp from './components/Auth/SignUp';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import EmailConfirmation from './components/Auth/EmailConfirmation';
import StudioInvitationModal from './components/StudioInvitationModal';
import PricingPage from './components/PricingPage';
import SubscriptionManagement from './components/SubscriptionManagement';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import StreakDisplay from './components/StreakDisplay';
import BrandLogo from './components/BrandLogo';
import { MusicalNoteIcon, ChartBarIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon, BeakerIcon, UserGroupIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import mixpanelService from './services/mixpanelService';
import subscriptionService from './services/subscriptionService';
import { getStudioInvitation } from './services/emailApi';
import apiClient from './utils/apiClient';
import { clearAuthData } from './utils/auth';

function App() {
  const [activeMode, setActiveMode] = useState('practice');

  // Function to handle mode changes with tracking
  const handleModeChange = (mode) => {
    setActiveMode(mode);
  };
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login', 'signup', 'forgot', 'reset', 'confirm'
  const [recoveryToken, setRecoveryToken] = useState('');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authUnavailable, setAuthUnavailable] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [pendingInvitation, setPendingInvitation] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);

  // Initialize Mixpanel on mount
  useEffect(() => {
    mixpanelService.init();
    mixpanelService.track('Application Opened', {
      entry_path: window.location.pathname,
      has_referrer: Boolean(document.referrer),
    });
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      setAuthUnavailable('');
      setUser(null);
      setIsAdmin(false);
      setIsTeacher(false);
      mixpanelService.resetUser();
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  useEffect(() => {
    if (activeMode === 'admin' && !isAdmin) {
      setActiveMode('practice');
    }
  }, [activeMode, isAdmin]);

  useEffect(() => {
    if (loading) return;

    const screenName = user
      ? activeMode.charAt(0).toUpperCase() + activeMode.slice(1)
      : `Authentication: ${authMode}`;
    mixpanelService.trackPageView(screenName);
  }, [activeMode, authMode, loading, user]);

  // Check for existing session on mount and handle email confirmation
  useEffect(() => {
    // Check if this is an email confirmation link
    const urlPath = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const invitationToken = params.get('invitation');
    const recoveryParams = new URLSearchParams(
      window.location.hash.replace(/^#/, '')
    );

    if (
      urlPath === '/reset-password' ||
      recoveryParams.get('type') === 'recovery'
    ) {
      setRecoveryToken(recoveryParams.get('access_token') || '');
      window.history.replaceState({}, document.title, '/reset-password');
      setAuthMode('reset');
      setLoading(false);
      return;
    }

    if (urlPath === '/signup') {
      setAuthMode('signup');
    }

    if (invitationToken) {
      // Keep the exact emailed token through signup, confirmation and login.
      // The API verifies the signed-in email before accepting it.
      localStorage.setItem(
        'pending_studio_invitation',
        JSON.stringify({ token: invitationToken })
      );
      params.delete('invitation');
      const remainingQuery = params.toString();
      window.history.replaceState(
        {},
        document.title,
        `${urlPath}${remainingQuery ? `?${remainingQuery}` : ''}`
      );
    }

    if (urlPath === '/confirm-email' || params.get('token')) {
      setAuthMode('confirm');
      setLoading(false);
      return;
    }

    // Check if this is a subscription success page
    if (urlPath === '/subscription/success') {
      setActiveMode('subscription-success');
    }

    const checkAuth = async () => {
      let invitationFromLink = null;
      if (invitationToken) {
        const invitationResult = await getStudioInvitation(invitationToken);
        if (invitationResult.success) {
          invitationFromLink = {
            ...invitationResult.invitation,
            token: invitationToken
          };
          localStorage.setItem(
            'pending_studio_invitation',
            JSON.stringify(invitationFromLink)
          );
        }
      }

      // Always try to verify authentication with the backend first
      // This will use HttpOnly cookies if they exist
      try {
        const response = await apiClient.get('/auth/me', {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setAuthUnavailable('');
          setUser(userData);
          setIsAdmin(Boolean(userData.is_admin));
          setIsTeacher(userData.role === 'teacher');
          mixpanelService.identifyUser(userData.id || userData.email, {
            email: userData.email,
            firstName: userData.first_name,
            lastName: userData.last_name,
            createdAt: userData.created_at,
            role: userData.role
          });

          if (invitationFromLink) {
            setPendingInvitation(invitationFromLink);
            setShowInvitationModal(true);
          }
        } else if (response.status === 401) {
          clearAuthData();
          setAuthUnavailable('');
          setUser(null);
          setIsAdmin(false);
          setIsTeacher(false);
        } else {
          throw new Error(`Session verification failed (${response.status})`);
        }
      } catch (error) {
        // A network or server failure is not proof that the session ended.
        // Keep the auth state indeterminate and offer an explicit retry rather
        // than presenting a misleading logged-out screen.
        setAuthUnavailable(
          'Practice Meter could not verify your session. Check your connection and try again.'
        );
        console.error('Auth check failed:', error);
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setAuthUnavailable('');
    setUser(userData);
    setIsAdmin(Boolean(userData.is_admin));
    setIsTeacher(userData.role === 'teacher');
    setAuthMode('login');

    // Apply the exclusion before emitting any authenticated event.
    mixpanelService.setUserContext(userData);
    mixpanelService.identifyUser(userData.id || userData.email, {
      email: userData.email,
      firstName: userData.first_name,
      lastName: userData.last_name,
      createdAt: userData.created_at,
      role: userData.role
    });
    mixpanelService.trackLogin(true, {
      user_role: userData.role || 'unknown'
    });

    // Check for pending studio invitation
    const storedInvitation = localStorage.getItem('pending_studio_invitation');
    if (storedInvitation) {
      try {
        const invitation = JSON.parse(storedInvitation);
        setPendingInvitation(invitation);
        setShowInvitationModal(true);
      } catch (e) {
        console.error('Error parsing pending invitation:', e);
        localStorage.removeItem('pending_studio_invitation');
      }
    }

  };

  const handleLogout = async () => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    clearAuthData();

    // Reset Mixpanel user identity
    mixpanelService.resetUser();

    setUser(null);
    setIsAdmin(false);
    setIsTeacher(false);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && authUnavailable) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          <BrandLogo className="mb-6" />
          <h1 className="text-2xl font-bold text-gray-900">
            We could not verify your session
          </h1>
          <p className="mt-3 text-gray-600">{authUnavailable}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 rounded-lg bg-indigo-600 px-5 py-3 font-medium text-white hover:bg-indigo-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Show auth screens if not logged in
  if (!user) {
    if (authMode === 'signup') {
      return (
        <SignUp
          onSignUpSuccess={handleLogin}
          onLoginClick={() => setAuthMode('login')}
        />
      );
    }

    if (authMode === 'forgot') {
      return (
        <ForgotPassword
          onBackToLogin={() => setAuthMode('login')}
        />
      );
    }

    if (authMode === 'reset') {
      return (
        <ResetPassword
          recoveryToken={recoveryToken}
          onComplete={() => {
            setRecoveryToken('');
            setAuthMode('login');
            window.history.replaceState({}, document.title, '/');
          }}
        />
      );
    }

    if (authMode === 'confirm') {
      return (
        <EmailConfirmation
          onLoginClick={() => setAuthMode('login')}
        />
      );
    }

    return (
      <Login
        onLogin={handleLogin}
        onSignUpClick={() => setAuthMode('signup')}
        onForgotPasswordClick={() => setAuthMode('forgot')}
      />
    );
  }

  // Main app for authenticated users
  return (
    // <WebSocketProvider>  // Commented out - not currently used
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-8">
                <BrandLogo compact />

                {/* Mode Tabs */}
                <nav className="flex space-x-1">
                  <button
                    onClick={() => handleModeChange('practice')}
                    className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                      activeMode === 'practice'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <MusicalNoteIcon className="h-5 w-5" />
                    <span className="font-medium">Practice</span>
                  </button>

                  <button
                    onClick={() => handleModeChange('monitor')}
                    className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                      activeMode === 'monitor'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <ChartBarIcon className="h-5 w-5" />
                    <span className="font-medium">Monitor</span>
                  </button>

                  {isTeacher && (
                    <button
                      onClick={() => handleModeChange('studio')}
                      className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                        activeMode === 'studio'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <UserGroupIcon className="h-5 w-5" />
                      <span className="font-medium">Studio</span>
                    </button>
                  )}

                  {isAdmin && (
                    <button
                      onClick={() => handleModeChange('admin')}
                      className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                        activeMode === 'admin'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <BeakerIcon className="h-5 w-5" />
                      <span className="font-medium">Admin</span>
                    </button>
                  )}
                </nav>
              </div>

              <div className="flex items-center space-x-4">
                {/* Streak Display */}
                <StreakDisplay user={user} />

                {/* User Info */}
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>

                {/* Subscription Button */}
                <button
                  onClick={() => handleModeChange('subscription')}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Manage Subscription"
                >
                  <CreditCardIcon className="h-6 w-6" />
                </button>

                {/* Settings Button */}
                <button
                  onClick={() => {
                    mixpanelService.track('Settings Opened');
                    setSettingsOpen(true);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Settings"
                >
                  <Cog6ToothIcon className="h-6 w-6" />
                </button>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          {activeMode === 'practice' && <PracticeMode user={user} />}
          {activeMode === 'monitor' && <MonitorMode user={user} />}
          {activeMode === 'studio' && isTeacher && <StudioView />}
          {activeMode === 'admin' && isAdmin && <AdminPanel />}
          {activeMode === 'pricing' && <PricingPage />}
          {activeMode === 'subscription' && <SubscriptionManagement />}
          {activeMode === 'subscription-success' && <SubscriptionSuccess />}
        </main>

        {/* Settings Modal */}
        <Settings
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />

        {/* Studio Modal - No longer needed as Studio is now a pane */}
        {/* {isTeacher && (
          <Studio isOpen={studioOpen} onClose={() => setStudioOpen(false)} />
        )} */}

        {/* Studio Invitation Modal */}
        <StudioInvitationModal
          invitation={pendingInvitation}
          isOpen={showInvitationModal}
          onClose={(accepted) => {
            setShowInvitationModal(false);
            setPendingInvitation(null);
            // If accepted, refresh user data to get updated studio_id
            if (accepted) {
              window.location.reload();
            }
          }}
        />
      </div>
    // </WebSocketProvider>  // Commented out - not currently used
  );
}

export default App;
